/**
 * Routines (slice 5 spec §2, §4). Pure: when a routine runs next, in its
 * workspace's IANA timezone, the schedule's rules, and what its run tells
 * the agent. The Worker writes `routines.next_run_at` from `nextRun` on every
 * routine write; the RoutineClock reads it.
 *
 * Slots are local wall-clock times. A time that doesn't exist (spring
 * forward) runs at the first minute after the gap; a time that happens twice
 * (fall back) runs at its first occurrence.
 */

export type RoutineKind = "repeat" | "once";

const EVERY_HOURS = [1, 2, 4] as const;

/** When a routine runs (spec §2.1, §2.2). Times are `HH:MM`, dates `YYYY-MM-DD`. */
export type Schedule = {
  kind: RoutineKind;
  /** 0 = Sunday. Empty for a reminder. */
  days: number[];
  time_of_day: string | null;
  every_hours: number | null;
  from_time: string | null;
  to_time: string | null;
  once_on: string | null;
};

/** What keeps a routine from running: off, paused for no visits, or ended. */
export type RoutineState = {
  enabled: boolean;
  paused_idle: boolean;
  ended_at: Date | null;
};

/** A run's slot: its instant, and its local wall-clock time (`routine_runs.local_at`). */
export type Slot = { at: Date; local: string };

/** A run is `missed` when it starts more than this after its slot (spec §4.4). */
const MISSED_AFTER_MS = 2 * 3_600_000;

const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;

// ── Local wall-clock time ────────────────────────────────────────────────

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/**
 * The local wall-clock time of `at` in `tz`, as milliseconds of a UTC date
 * with the same fields ("naive"): arithmetic on it is calendar arithmetic.
 */
function naive(at: number, tz: string): number {
  const p: Record<string, number> = {};
  for (const part of formatter(tz).formatToParts(new Date(at))) {
    if (part.type !== "literal") p[part.type] = Number(part.value);
  }
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** `tz`'s offset from UTC at `at`, in ms (local − UTC), to the minute. */
function offset(at: number, tz: string): number {
  const whole = Math.floor(at / 1000) * 1000;
  return naive(whole, tz) - whole;
}

/**
 * The instant a local wall-clock time (naive ms) happens in `tz`: its first
 * occurrence when it happens twice; for a time in a spring-forward gap, the
 * first minute after the gap.
 */
function instantOf(local: number, tz: string): number {
  const before = offset(local - DAY_MS / 2, tz);
  const after = offset(local + DAY_MS / 2, tz);
  const fits = [local - before, local - after].filter((t) => naive(t, tz) === local);
  if (fits.length) return Math.min(...fits);
  // In the gap: the transition is between the two readings. Find it to the minute.
  let lo = local - after;
  let hi = local - before;
  while (hi - lo > MINUTE_MS) {
    const mid = lo + Math.floor((hi - lo) / 2 / MINUTE_MS) * MINUTE_MS;
    if (offset(mid, tz) === before) lo = mid;
    else hi = mid;
  }
  return hi;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** A naive ms as `YYYY-MM-DD`. */
const dateOf = (n: number) => new Date(n).toISOString().slice(0, 10);

/** `at`'s local date in `tz`, `YYYY-MM-DD`. */
export function localDate(at: Date, tz: string): string {
  return dateOf(naive(at.getTime(), tz));
}

/** The day before `day` (`YYYY-MM-DD`), by the calendar (slice 1m: today's and yesterday's notes). */
export function dayBefore(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** `at`'s local wall-clock time in `tz`, `YYYY-MM-DD HH:MM` (a `timestamp` without a zone). */
export function localStamp(at: Date, tz: string): string {
  const n = new Date(naive(at.getTime(), tz));
  return `${dateOf(n.getTime())} ${pad(n.getUTCHours())}:${pad(n.getUTCMinutes())}`;
}

/** `HH:MM` (or `HH:MM:SS`) as minutes after midnight; NaN when it isn't one. */
function minutesOf(time: string): number {
  const m = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/.exec(time);
  return m ? Number(m[1]) * 60 + Number(m[2]) : Number.NaN;
}

/** `YYYY-MM-DD` as a naive ms at midnight; NaN when it isn't a real date. */
function dayOf(date: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return Number.NaN;
  const n = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return dateOf(n) === date ? n : Number.NaN;
}

/** The instant `date` at `time` happens in `tz` (spring forward and fall back as above). */
export function zonedTime(date: string, time: string, tz: string): Date {
  return new Date(instantOf(dayOf(date) + minutesOf(time) * MINUTE_MS, tz));
}

// ── The next run ─────────────────────────────────────────────────────────

/** An hourly routine's slots in a day, in minutes: `from`, then every `every` hours, to `to`. */
export function hourlySlots(from: string, to: string, every: number): number[] {
  const start = minutesOf(from);
  const end = minutesOf(to);
  const out: number[] = [];
  for (let m = start; m <= end; m += every * 60) out.push(m);
  return out;
}

/** The minutes after midnight a repeating routine runs at, each day it runs. */
function dailyMinutes(s: Schedule): number[] {
  if (s.every_hours) return hourlySlots(s.from_time!, s.to_time!, s.every_hours);
  return s.time_of_day ? [minutesOf(s.time_of_day)] : [];
}

/** A week and a day: every repeating schedule has a slot in it, DST included. */
const SEARCH_DAYS = 9;

/**
 * The first slot of `s` in `tz` later than `after` (spec §4.1); a reminder's
 * one slot whenever it is, a missed one included. Null for a repeating
 * routine with no days.
 */
function nextSlot(s: Schedule, tz: string, after: Date): Slot | null {
  if (s.kind === "once") return reminderSlot(s, tz);
  const minutes = dailyMinutes(s);
  if (s.days.length === 0 || minutes.length === 0) return null;
  const start = Date.UTC(...ymd(naive(after.getTime(), tz)));
  for (let d = 0; d < SEARCH_DAYS; d++) {
    const day = start + d * DAY_MS;
    if (!s.days.includes(new Date(day).getUTCDay())) continue;
    const at = minutes
      .map((m) => instantOf(day + m * MINUTE_MS, tz))
      .find((t) => t > after.getTime());
    if (at !== undefined) return slotAt(new Date(at), tz);
  }
  return null;
}

const slotAt = (at: Date, tz: string): Slot => ({ at, local: localStamp(at, tz) });

/** A reminder's one slot, `once_on` at `time_of_day`. */
function reminderSlot(s: Schedule, tz: string): Slot | null {
  if (!s.once_on || !s.time_of_day) return null;
  return slotAt(zonedTime(s.once_on, s.time_of_day, tz), tz);
}

function ymd(n: number): [number, number, number] {
  const d = new Date(n);
  return [d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()];
}

/**
 * `routines.next_run_at` (spec §4.1): null while the routine is off, paused
 * for no visits or ended; else its next slot after `after`.
 */
export function nextRun(r: Schedule & RoutineState, tz: string, after: Date): Slot | null {
  if (!r.enabled || r.paused_idle || r.ended_at) return null;
  return nextSlot(r, tz, after);
}

/**
 * Whether a run of the slot at `scheduledFor`, starting at `now`, is
 * `missed` (spec §4.4): over 2 hours late, or the routine's following slot
 * has already passed (an outage).
 */
export function isMissed(s: Schedule, tz: string, scheduledFor: Date, now: Date): boolean {
  if (now.getTime() - scheduledFor.getTime() > MISSED_AFTER_MS) return true;
  const following = s.kind === "repeat" ? nextSlot(s, tz, scheduledFor) : null;
  return following !== null && following.at.getTime() <= now.getTime();
}

// ── The schedule's rules (spec §2.2, §6.1) ───────────────────────────────

/** A routine's fields as a person or the agent gives them. */
export type ScheduleInput = {
  time?: unknown;
  days?: unknown;
  on?: unknown;
  every?: unknown;
  from?: unknown;
  to?: unknown;
};

export type ScheduleCheck = { ok: true; schedule: Schedule } | { ok: false; hint: string };

const bad = (hint: string): ScheduleCheck => ({ ok: false, hint });
const given = (v: unknown) => v !== undefined && v !== null;
const isTime = (v: unknown): v is string => typeof v === "string" && !Number.isNaN(minutesOf(v));
const asTime = (v: string) => v.slice(0, 5);

/** A reminder is at least this far ahead, and at most a year (spec §6.1). */
export const REMINDER_MIN_AHEAD_MS = 2 * MINUTE_MS;
export const REMINDER_MAX_AHEAD_MS = 366 * DAY_MS;

/**
 * The schedule from a request's fields (spec §6.1): exactly one of `days`
 * and `on`; with `days`, exactly one of `time` and `every`, which needs
 * `from` before `to`; a reminder takes `time`, at least 2 minutes ahead and
 * within a year of `now` in `tz`.
 */
export function checkSchedule(input: ScheduleInput, tz: string, now: Date): ScheduleCheck {
  if (given(input.days) === given(input.on)) return bad("days or on: give exactly one.");
  if (given(input.on)) return checkReminder(input, tz, now);
  const days = checkDays(input.days);
  if (!days) return bad("days: 1 to 7 different days, 0 = Sunday to 6 = Saturday.");
  if (given(input.time) === given(input.every)) return bad("time or every: give exactly one.");
  return given(input.time) ? checkAtTime(input, days) : checkHourly(input, days);
}

/** A reminder: `on` at `time`, at least 2 minutes ahead and within a year. */
function checkReminder(input: ScheduleInput, tz: string, now: Date): ScheduleCheck {
  if (typeof input.on !== "string" || Number.isNaN(dayOf(input.on))) {
    return bad("on: a date, YYYY-MM-DD.");
  }
  if (given(input.every) || given(input.from) || given(input.to)) {
    return bad("every: a reminder runs once, at a time.");
  }
  if (!isTime(input.time)) return bad("time: HH:MM, 24-hour.");
  const ahead = zonedTime(input.on, input.time, tz).getTime() - now.getTime();
  if (ahead < REMINDER_MIN_AHEAD_MS) return bad("on: at least 2 minutes from now.");
  if (ahead > REMINDER_MAX_AHEAD_MS) return bad("on: within a year.");
  const schedule = { ...repeat([], { time_of_day: asTime(input.time) }), kind: "once" as const };
  return { ok: true, schedule: { ...schedule, once_on: input.on } };
}

/** 1 to 7 different days, sorted; null when they aren't. */
function checkDays(days: unknown): number[] | null {
  if (!Array.isArray(days) || days.length < 1 || days.length > 7) return null;
  if (!days.every((d) => Number.isInteger(d) && d >= 0 && d <= 6)) return null;
  if (new Set(days).size !== days.length) return null;
  return [...(days as number[])].sort((a, b) => a - b);
}

function checkAtTime(input: ScheduleInput, days: number[]): ScheduleCheck {
  if (!isTime(input.time)) return bad("time: HH:MM, 24-hour.");
  if (given(input.from) || given(input.to)) return bad("from: only with every.");
  return { ok: true, schedule: repeat(days, { time_of_day: asTime(input.time) }) };
}

/** By the hour: every 1, 2 or 4 hours, `from` before `to`. */
function checkHourly(input: ScheduleInput, days: number[]): ScheduleCheck {
  if (!(EVERY_HOURS as readonly unknown[]).includes(input.every)) {
    return bad("every: 1, 2 or 4 hours.");
  }
  if (!isTime(input.from)) return bad("from: HH:MM, 24-hour.");
  if (!isTime(input.to)) return bad("to: HH:MM, 24-hour.");
  if (minutesOf(input.from) >= minutesOf(input.to)) return bad("from: before to.");
  const schedule = repeat(days, {
    every_hours: input.every as number,
    from_time: asTime(input.from),
    to_time: asTime(input.to),
  });
  return { ok: true, schedule };
}

function repeat(days: number[], when: Partial<Schedule>): Schedule {
  return {
    kind: "repeat",
    days,
    time_of_day: null,
    every_hours: null,
    from_time: null,
    to_time: null,
    once_on: null,
    ...when,
  };
}

// ── The schedule in words (spec §6.1, §11.4) ─────────────────────────────

/** "6:30 pm", from `HH:MM` or a local stamp that ends in it. */
export function clockLabel(local: string): string {
  const [h, m] = local.slice(-5).split(":").map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${pad(m)} ${h < 12 ? "am" : "pm"}`;
}

/** A window's end: "8 am", or "8:30 am" off the hour. */
const hourLabel = (time: string) => clockLabel(time).replace(":00 ", " ");

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYS_PLURAL = [
  "Sundays",
  "Mondays",
  "Tuesdays",
  "Wednesdays",
  "Thursdays",
  "Fridays",
  "Saturdays",
];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The days as a preset says them (spec §2.2): "Weekdays", "Mondays", "Mon, Wed"; "" for every day. */
function daysLabel(days: number[]): string {
  const set = [...new Set(days)].sort((a, b) => a - b);
  if (set.length === 7) return "";
  if (set.join() === "1,2,3,4,5") return "Weekdays";
  if (set.join() === "0,6") return "Weekends";
  if (set.length === 1) return DAYS_PLURAL[set[0]];
  return set.map((d) => DAY_NAMES[d]).join(", ");
}

/**
 * The schedule as one phrase: "Mondays 8:00 am", "Every day at 7:00 am",
 * "Every 2 hours, 8 am–6 pm, weekdays", or a reminder's "Thu 15 Oct, 6:30 pm".
 */
export function whenLabel(s: Schedule): string {
  if (s.kind === "once" && s.once_on) {
    const day = dayOf(s.once_on);
    const [, month, date] = ymd(day);
    const weekday = DAY_NAMES[new Date(day).getUTCDay()];
    return `${weekday} ${date} ${MONTHS[month]}, ${clockLabel(s.time_of_day ?? "00:00")}`;
  }
  const days = daysLabel(s.days);
  if (s.every_hours) {
    const every = s.every_hours === 1 ? "Every hour" : `Every ${s.every_hours} hours`;
    const window = `${hourLabel(s.from_time!)}–${hourLabel(s.to_time!)}`;
    const on = days
      ? `, ${days === "Weekdays" || days === "Weekends" ? days.toLowerCase() : days}`
      : "";
    return `${every}, ${window}${on}`;
  }
  const time = clockLabel(s.time_of_day ?? "00:00");
  return days ? `${days} ${time}` : `Every day at ${time}`;
}

// ── What a run tells the agent (spec §5.1) ───────────────────────────────

const HEAD_FORMAT = new Map<string, Intl.DateTimeFormat>();

/** "Thursday 9 October" in `tz`. */
function dayLabel(at: Date, tz: string): string {
  let f = HEAD_FORMAT.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    HEAD_FORMAT.set(tz, f);
  }
  return f.format(at).replace(",", "");
}

/** A last done run's summary in the claim: at most this many characters. */
const LAST_RUN_MAX = 1_500;

export type ClaimInput = {
  kind: RoutineKind;
  title: string;
  instructions: string;
  seeded: boolean;
  text_me: boolean;
  /** The routine's person's name, on one line. */
  person: string;
  /** The run's slot (now for Run now). */
  at: Date;
  timezone: string;
  /** The last done run of a repeating routine. */
  last: { at: Date; summary: string } | null;
};

/**
 * The claim's text (spec §5.1): the `[Crew]` line naming the routine, its
 * person and the local time; that it's theirs alone; the last done run's
 * summary, as already sent; `routine_notify`'s
 * rule when Text it to me is on; then the instructions.
 */
export function routineClaim(c: ClaimInput): string {
  const what = c.kind === "once" ? "Reminder" : "Routine";
  const seeded = c.seeded ? " (the morning note)" : "";
  const time = localStamp(c.at, c.timezone).slice(11);
  const lines = [
    `[Crew] ${what} "${c.title}"${seeded}, for ${c.person}. It's ${dayLabel(c.at, c.timezone)}, ${time} (${c.timezone}).`,
  ];
  // A run is its person's own (slice 5 §6.4): board() is theirs here, not the crew's.
  lines.push(
    `It's ${c.person}'s own: board() here lists only their tasks, and what got done for them since the last run. Leave out the rest of the crew's, unless the instructions ask for it: then board({ mine: false }).`,
  );
  if (c.kind === "repeat" && c.last) {
    // Context, not news: what it said was done is never "since yesterday" again.
    const summary = c.last.summary.slice(0, LAST_RUN_MAX);
    lines.push(
      `Last run, ${dayLabel(c.last.at, c.timezone)}, already sent (don't repeat it as new): ${summary}`,
    );
  }
  if (c.kind === "repeat" && c.text_me) {
    lines.push(
      `Crew texts this to ${c.person} only if you call routine_notify. Call it once if the instructions say to text it every time; otherwise, only when something in it needs them today.`,
    );
  }
  return `${lines.join("\n")}\n\n${c.instructions}`;
}
